import { DigioUploadPayload, K2DigioRecord } from "../../remittance-beta/types/digio/K2Digio";
import { isPurposeCodeAllowedForDigio } from "../../remittance-beta/utils/digioStatus";
import { isAdIIPurpose } from "../../remittance-beta/utils/remitterKycFlow";

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && String(value).trim() !== "";

/**
 * History Digio A2 e-sign — no featureSet.
 * Requires AD-II + new KYC used + digioEsignId + purposeCode in allowedPurposeCodes.
 */
export const isDigioHistoryEsignEnabled = (trxnData?: any | null): boolean => {
  if (!trxnData) {
    return false;
  }

  const adIiNewKycUsed = Boolean(trxnData.adIiKyc?.adIiNewKycUsed ?? trxnData.adIiNewKycUsed);
  if (!isAdIIPurpose(trxnData.adCategory) || !adIiNewKycUsed) {
    return false;
  }

  if (!hasValue(trxnData.digioEsignId)) {
    return false;
  }

  const purposeCode = trxnData.otherDetails?.purposeCode ?? trxnData.purposeCode;
  const allowedPurposeCodes = trxnData.digioEsignDetails?.allowedPurposeCodes;

  return isPurposeCodeAllowedForDigio(allowedPurposeCodes, purposeCode);
};

const pickNumber = (...values: unknown[]) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") {
      continue;
    }
    const num = Number(value);
    if (!Number.isNaN(num)) {
      return num;
    }
  }
  return 0;
};

const pickString = (...values: unknown[]) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }
    const str = String(value).trim();
    if (str) {
      return str;
    }
  }
  return "";
};

export const buildDigioPayloadFromHistory = (
  trxnData: any,
  sendType: "email" | "sms",
  loggedInUserKey: string,
  transactionNumberFallback?: string
): DigioUploadPayload => {
  const remitter = trxnData?.remitterAndBeneDetails ?? {};
  const digio = (trxnData?.digioEsignDetails ?? null) as K2DigioRecord | null;
  const payment = trxnData?.paymentBreakUp ?? {};
  const nameParts = pickString(remitter.remitterName, digio?.signerName)
    .split(/\s+/)
    .filter(Boolean);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";
  const cityStateParts = pickString(remitter.remitterCityAndState)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const city = pickString(remitter.city, remitter.remitterCity, cityStateParts[0]);
  const remitterState = pickString(remitter.state, remitter.remitterState, cityStateParts[1]);
  const beneNameParts = pickString(remitter.beneficiaryName).split(/\s+/).filter(Boolean);
  const panDoc = trxnData?.kycDocs?.userDocuments?.find(
    (doc: { docName?: string }) => doc?.docName === "PAN_CARD"
  );

  const transactionNumber = pickString(
    digio?.transactionNumber,
    trxnData?.transactionNumber,
    trxnData?.declarationDetails?.transactionNumber,
    transactionNumberFallback
  );

  const payload: DigioUploadPayload = {
    userKey: pickString(digio?.userKey, trxnData?.kycDocs?.userKey),
    loggedInUserKey: pickString(digio?.loggedInUserKey, loggedInUserKey),
    requestType: "k2",
    purpose_code: pickString(trxnData?.otherDetails?.purposeCode),
    purpose_description: pickString(
      trxnData?.purpose,
      trxnData?.otherDetails?.purposeOfPayment,
      digio?.transactionPurpose
    ),
    signerName: pickString(remitter.remitterName, digio?.signerName),
    sendType,
    pan_num: pickString(panDoc?.idNumber, remitter.panNumber, remitter.pan_num),
    city,
    dest_curr: pickString(
      payment.destinationCurrency,
      payment.dest_curr,
      payment.destCurrency,
      remitter.destinationCurrency
    ),
    pincode: pickString(remitter.pincode, remitter.pinCode, remitter.pin_code),
    recipientId: pickNumber(
      remitter.recipientId,
      remitter.beneficiaryId,
      remitter.beneId,
      trxnData?.recipientId
    ),
    reciepient_recieve: pickNumber(payment.theyReceive, payment.reciepient_recieve),
    recipeint_accnt_num: pickString(remitter.beneficiaryBankAccountNumber),
    recipient_bank_name: pickString(remitter.beneficiaryBankName),
    recipient_first_name: beneNameParts[0] || "",
    recipient_last_name: beneNameParts.slice(1).join(" ") || "",
    reicpient_bank_accnt_country: pickString(remitter.beneficiaryBankAccountCountry),
    state: pickString(
      remitter.beneficiaryBankAccountCountry,
      remitter.beneficiaryCountryOfResidence,
      remitterState
    ),
    total_pay: pickNumber(payment.totalPay, payment.total_pay),
    user_address1: pickString(remitter.remitterAddress, remitter.addressLine1),
    user_first_name: firstName,
    user_last_name: lastName,
    bene: Boolean(remitter.isBene),
  };

  if (transactionNumber) {
    payload.transactionNumber = transactionNumber;
  }

  if (sendType === "email") {
    payload.email = pickString(remitter.remitterEmail, digio?.email);
  } else {
    payload.phone = pickString(remitter.remitterPhoneNumber, digio?.phone);
  }

  return payload;
};
